/**
 * Remove one key from a project (the drawer's Remove key): `setAccountKeys` with that key left out and
 * no new path. The project's last key is Unlink's job, so the caller opens that confirm instead, and
 * a server that still finds the set empty hands over to it too (`onKeysEmpty`).
 */
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { TruncateWithTooltip } from '@/_shared/components/TruncateWithTooltip';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import { useOrpc } from '@/_shared/hooks/useOrpc';

import { type LinkedProject, type LinkedProjectKey, projectLabel } from '../../lib/linkedRepoList';
import { rpcErrorCode, rpcErrorMessage } from './linkedRepoSchema';
import { useInvalidateLinkedRepos } from './useInvalidateLinkedRepos';

export interface RemoveKeyTarget {
  project: LinkedProject;
  key: LinkedProjectKey;
}

export function RemoveKeyDialog({
  target,
  onOpenChange,
  onKeysEmpty,
}: {
  target: RemoveKeyTarget | null;
  onOpenChange: (open: boolean) => void;
  onKeysEmpty: (project: LinkedProject) => void;
}): React.JSX.Element {
  const orpc = useOrpc();
  const invalidate = useInvalidateLinkedRepos();
  const remove = useMutation(orpc.warehouseProjects.setAccountKeys.mutationOptions());
  const pending = remove.isPending;

  const targetId = target ? `${target.project.groupId}|${target.key.accountKey}` : null;
  useEffect(() => {
    remove.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the target changes; the form and mutation are stable
  }, [targetId]);

  const confirm = (): void => {
    if (!target) return;
    const { project, key } = target;
    remove.mutate(
      {
        repoFullName: project.repoFullName,
        projectPath: project.projectPath,
        accountKeys: project.keys.filter((k) => k.accountKey !== key.accountKey).map((k) => k.accountKey),
        // The set this removal starts from: if another admin changed the keys meanwhile, the server
        // refuses (CONFLICT, "changed elsewhere") instead of dropping the key they added.
        expectedAccountKeys: project.keys.map((k) => k.accountKey),
      },
      {
        onSuccess: () => {
          invalidate();
          toast.success(`Removed ${key.display.database} from ${projectLabel(project)}.`);
          onOpenChange(false);
        },
        onError: (err) => {
          // Changed elsewhere: refetch the list, so the drawer shows the keys as they are now.
          if (rpcErrorCode(err) === 'CONFLICT') invalidate();
          // The set came back empty on the server (another edit removed the other keys): that is Unlink.
          if (rpcErrorCode(err) === 'BAD_REQUEST' && /empty|at least one|no account key/i.test(err.message)) {
            onOpenChange(false);
            onKeysEmpty(project);
          }
        },
      },
    );
  };

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(next) => {
        if (!next && !pending) onOpenChange(false);
      }}
    >
      <DialogContent
        closeDisabled={pending}
        onEscapeKeyDown={(e) => {
          if (pending) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (pending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex min-w-0 items-baseline gap-1">
            <span className="shrink-0">Remove</span>
            <TruncateWithTooltip focusable>{target?.key.display.database ?? ''}</TruncateWithTooltip>
            <span className="shrink-0">?</span>
          </DialogTitle>
          <DialogDescription className="min-w-0 break-words">
            {target && (
              <>
                Remove {target.key.display.database} from {projectLabel(target.project)}? If no other repository uses
                it, its docs are deleted.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        {remove.isError && (
          <Callout variant="error">{rpcErrorMessage(remove.error, 'Could not remove the key.')}</Callout>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            disabled={pending}
            onClick={() => onOpenChange(false)}
            label="Cancel"
          />
          <Button
            type="button"
            variant="primary-destructive"
            loading={pending}
            onClick={confirm}
            label={pending ? 'Removing…' : 'Remove key'}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
